import { useParams, useNavigate } from "react-router-dom";
import { UserEditForm } from "@/components/users";
import { LoadingSpinner } from "@/components/common/LoadingSpinner";
import { ErrorFallback } from "@/components/common/ErrorFallback";
import { PageHeader } from "@/components/common/page-header";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import { usePermissions } from "@/hooks/usePermissions";
import { useUserDetail, useRevokeUserAgreement } from "@/hooks/useUserQueries";
import { AgreementCommitmentsCard } from "@/components/common/AgreementCommitmentsCard";

export function UserEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    data: user,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useUserDetail(id!);
  const revokeAgreement = useRevokeUserAgreement(id!);

  const { hasRole } = usePermissions();
  const canRevoke = hasRole("SuperAdmin");

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Full-screen only when there is nothing to edit at all — see UserDetailPage for the same fix
  // and the reason: a failed background refetch must not throw away a user still in the cache.
  if (!user) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Edit User"
          subtitle="Update user information and permissions"
          back={{
            label: "Back to Users",
            onClick: () => navigate("/admin/users"),
          }}
        />
        <ErrorFallback error={error as Error} onRetry={() => refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Edit User"
        subtitle="Update user information and permissions"
        back={{
          label: "Back to User",
          onClick: () => navigate(`/admin/users/${user.id}`),
        }}
      />

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Could not refresh user details</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>The form below may be showing a stale copy of this user.</p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Form */}
      <div className="max-w-2xl space-y-6">
        <UserEditForm user={user} />
        <AgreementCommitmentsCard
          statuses={user.agreements}
          canRevoke={canRevoke}
          description={`Every acceptance of this commitment by user ${user.email} is removed, so they are asked for it again before the console or a new machine can be used.`}
          // Clears only each tenant's own record: the gate reads every person's own column, so the
          // other members keep theirs. Revoking from the tenant is what asks every member again.
          includeRelatedLabel="Also clear the record on this user's tenants (other members keep theirs)"
          isPending={revokeAgreement.isPending}
          onRevoke={(type, includeTenants) =>
            revokeAgreement.mutateAsync({ type, includeTenants })
          }
        />
      </div>
    </div>
  );
}
